/**
 * Releases a Three.js WebGLRenderer that will never be used again.
 *
 * WebGLRenderer.dispose() frees Three.js-managed GPU resources, but the
 * browser may keep the underlying WebGL context alive until the canvas is
 * garbage-collected. Short-lived previews and print bakes can therefore hit
 * the browser's active-context limit despite calling dispose(). Disposing
 * first removes Three.js's context-loss listener; forcing the loss afterwards
 * then returns the context immediately without the expected loss being logged
 * as an unexpected renderer failure.
 */
export function releaseWebGLRenderer(renderer, { removeCanvas = true } = {}) {
  if (!renderer) return;
  const canvas = renderer.domElement;

  try {
    renderer.setAnimationLoop?.(null);
  } catch {
    // A partially initialized/lost renderer should still reach the remaining
    // cleanup steps.
  }
  try {
    renderer.dispose?.();
  } catch {
    // Context release below is the important final fallback.
  }
  try {
    renderer.forceContextLoss?.();
  } catch {
    // WEBGL_lose_context is optional; GC remains the browser fallback.
  }
  if (removeCanvas) canvas?.remove?.();
}

/**
 * Reads the texture-size limit from an already active renderer. Reusing the
 * application's persistent renderer avoids creating a throwaway WebGL context
 * merely to query one capability value.
 */
export function maxWebGLRenderEdgePx(renderer, fallback = 4096, safetyFactor = 0.9) {
  try {
    const gl = renderer?.getContext?.();
    const maxTextureSize = gl?.getParameter?.(gl.MAX_TEXTURE_SIZE);
    if (Number.isFinite(maxTextureSize) && maxTextureSize > 0) {
      return Math.max(1, Math.floor(maxTextureSize * safetyFactor));
    }
  } catch {
    // Lost/unavailable contexts fall back to a conservative render edge.
  }
  return fallback;
}
